// ============================================
// OFFICEFORGED — ProseMirror Editor (Full)
// Document canvas with card-aware editing.
// Full formatting toolbar wired to ProseMirror commands.
// ============================================

import { EditorState, Plugin } from 'prosemirror-state';
import { EditorView, Decoration, DecorationSet } from 'prosemirror-view';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap, toggleMark, setBlockType, lift, joinUp } from 'prosemirror-commands';
import { history, undo, redo } from 'prosemirror-history';
import { inputRules, textblockTypeInputRule } from 'prosemirror-inputrules';
import { wrapInList, splitListItem, liftListItem, sinkListItem } from 'prosemirror-schema-list';
import { findWrapping } from 'prosemirror-transform';
import { schema } from './schema.js';
import { TableCardView } from './table-card.js';
import { createFormulaBridgePlugin, refreshFormulas } from './formula-bridge.js';

export class Editor {
  constructor(cardEngine, cardPanel) {
    this.cardEngine = cardEngine;
    this.cardPanel = cardPanel;
    this.view = null;
    this._autoSaveTimer = null;
    this.init();
  }

  init() {
    const editorEl = document.getElementById('editor');
    const self = this;

    // Input rules: # → H1, ## → H2, ### → H3
    const headingRule = (level) => textblockTypeInputRule(
      new RegExp(`^(#{${level}})\\s$`),
      schema.nodes.heading,
      () => ({ level })
    );

    const rules = inputRules({
      rules: [headingRule(1), headingRule(2), headingRule(3)]
    });

    // Card sync plugin
    const cardSyncPlugin = new Plugin({
      view() {
        return {
          update(view, prevState) {
            if (view.state.doc !== prevState.doc) {
              self._syncCards(view.state);
            }
          }
        };
      }
    });

    // Placeholder plugin
    const placeholderPlugin = new Plugin({
      props: {
        decorations(state) {
          const { doc } = state;
          if (doc.childCount === 1 && doc.firstChild.isTextblock && doc.firstChild.content.size === 0) {
            return DecorationSet.create(doc, [
              Decoration.node(0, doc.firstChild.nodeSize, { class: 'placeholder' })
            ]);
          }
          return null;
        }
      }
    });

    const state = EditorState.create({
      schema,
      doc: schema.node('doc', null, [
        schema.node('paragraph', { cardId: null }, [])
      ]),
      plugins: [
        rules,
        keymap({
          'Mod-z': undo,
          'Mod-y': redo,
          'Mod-Shift-z': redo,
          'Mod-b': toggleMark(schema.marks.strong),
          'Mod-i': toggleMark(schema.marks.em),
          'Mod-u': toggleMark(schema.marks.underline),
          'Mod-Shift-x': toggleMark(schema.marks.strikethrough),
          'Mod-s': () => { self._saveToStorage(); return true; },
          'Mod-p': () => { self._print(); return true; },
          'Enter': splitListItem(schema.nodes.list_item),
          'Tab': sinkListItem(schema.nodes.list_item),
          'Shift-Tab': liftListItem(schema.nodes.list_item)
        }),
        keymap(baseKeymap),
        history(),
        createFormulaBridgePlugin(this.cardEngine),
        cardSyncPlugin,
        placeholderPlugin
      ]
    });

    this.view = new EditorView(editorEl, {
      state,
      nodeViews: {
        table_card: (node, view, getPos) => {
          return new TableCardView(node, view, getPos, this.cardEngine);
        }
      },
      dispatchTransaction: (tr) => {
        const newState = this.view.state.apply(tr);
        this.view.updateState(newState);
        this._updateToolbarState(newState);
        if (tr.docChanged) self._scheduleAutoSave();

        // Format Painter: apply copied marks on next selection
        if (this._formatPainterActive && tr.selectionSet && !tr.docChanged) {
          const { from, to } = newState.selection;
          if (from !== to && this._copiedMarks) {
            let applyTr = this.view.state.tr;
            // Clear existing marks first
            Object.keys(schema.marks).forEach(markName => {
              applyTr = applyTr.removeMark(from, to, schema.marks[markName]);
            });
            // Apply copied marks
            this._copiedMarks.forEach(m => {
              const markType = schema.marks[m.type];
              if (markType) {
                applyTr = applyTr.addMark(from, to, markType.create(m.attrs));
              }
            });
            this.view.dispatch(applyTr);
            this._formatPainterActive = false;
            const btn = document.getElementById('btn-format-painter');
            if (btn) btn.classList.remove('active');
          }
        }
      }
    });

    this._setupToolbar();

    // Listen for card updates to refresh formulas
    // and card insert requests from library panel
    this.cardEngine.on((event, data) => {
      if (event === 'card:updated' || event === 'library:updated') {
        refreshFormulas(this.view);
      }
      if (event === 'card:insert-request' && data) {
        this._insertCardContent(data);
      }
    });

    this._syncCards(this.view.state);
    this._setupDragDrop();
    this._setupCardMention();
    this._setupFeedClickHandler();
    this._loadPageSetup();
    setTimeout(() => this._loadSavedDoc(), 100);
  }

  // ==========================================
  // TOOLBAR COMMANDS
  // ==========================================

  _setupToolbar() {
    // Undo / Redo
    this._btn('btn-undo', () => undo(this.view.state, this.view.dispatch));
    this._btn('btn-redo', () => redo(this.view.state, this.view.dispatch));

    // Block type dropdown
    const blockSel = document.getElementById('sel-block-type');
    if (blockSel) {
      blockSel.addEventListener('change', () => {
        const val = blockSel.value;
        if (val === 'paragraph') {
          setBlockType(schema.nodes.paragraph)(this.view.state, this.view.dispatch);
        } else if (val.startsWith('h')) {
          const level = parseInt(val.charAt(1));
          setBlockType(schema.nodes.heading, { level })(this.view.state, this.view.dispatch);
        } else if (val === 'blockquote') {
          this._wrapIn(schema.nodes.blockquote);
        } else if (val === 'code_block') {
          setBlockType(schema.nodes.code_block)(this.view.state, this.view.dispatch);
        }
        this.view.focus();
      });
    }

    // Font family dropdown
    const fontSel = document.getElementById('sel-font-family');
    if (fontSel) {
      fontSel.addEventListener('change', () => {
        const val = fontSel.value;
        if (val) {
          this._applyMark(schema.marks.fontFamily, { family: val });
        } else {
          this._removeMark(schema.marks.fontFamily);
        }
        this.view.focus();
      });
    }

    // Font size dropdown
    const sizeSel = document.getElementById('sel-font-size');
    if (sizeSel) {
      sizeSel.addEventListener('change', () => {
        const val = sizeSel.value;
        if (val) {
          this._applyMark(schema.marks.fontSize, { size: val });
        } else {
          this._removeMark(schema.marks.fontSize);
        }
        this.view.focus();
      });
    }

    // Inline formatting
    this._btn('btn-bold', () => toggleMark(schema.marks.strong)(this.view.state, this.view.dispatch));
    this._btn('btn-italic', () => toggleMark(schema.marks.em)(this.view.state, this.view.dispatch));
    this._btn('btn-underline', () => toggleMark(schema.marks.underline)(this.view.state, this.view.dispatch));
    this._btn('btn-strike', () => toggleMark(schema.marks.strikethrough)(this.view.state, this.view.dispatch));
    this._btn('btn-superscript', () => toggleMark(schema.marks.superscript)(this.view.state, this.view.dispatch));
    this._btn('btn-subscript', () => toggleMark(schema.marks.subscript)(this.view.state, this.view.dispatch));

    // Text color
    const textColorPick = document.getElementById('pick-text-color');
    const textColorBar = document.getElementById('bar-text-color');
    this._btn('btn-text-color', () => {
      const color = textColorPick.value;
      this._applyMark(schema.marks.textColor, { color });
      this.view.focus();
    });
    if (textColorPick) {
      textColorPick.addEventListener('input', () => {
        textColorBar.style.background = textColorPick.value;
      });
    }

    // Highlight
    const hlPick = document.getElementById('pick-highlight');
    const hlBar = document.getElementById('bar-highlight');
    this._btn('btn-highlight', () => {
      const color = hlPick.value;
      this._applyMark(schema.marks.highlight, { color });
      this.view.focus();
    });
    if (hlPick) {
      hlPick.addEventListener('input', () => {
        hlBar.style.background = hlPick.value;
      });
    }

    // Link
    this._btn('btn-link', () => {
      const { state } = this.view;
      const { from, to } = state.selection;
      if (from === to) { alert('Select text first to add a link.'); return; }
      const href = prompt('Enter URL:');
      if (href) {
        this._applyMark(schema.marks.link, { href });
      }
      this.view.focus();
    });

    // Alignment
    this._btn('btn-align-left', () => this._setAlignment('left'));
    this._btn('btn-align-center', () => this._setAlignment('center'));
    this._btn('btn-align-right', () => this._setAlignment('right'));
    this._btn('btn-align-justify', () => this._setAlignment('justify'));

    // Lists
    this._btn('btn-bullet-list', () => {
      wrapInList(schema.nodes.bullet_list)(this.view.state, this.view.dispatch);
      this.view.focus();
    });
    this._btn('btn-ordered-list', () => {
      wrapInList(schema.nodes.ordered_list)(this.view.state, this.view.dispatch);
      this.view.focus();
    });

    // Indent / Outdent (for lists)
    this._btn('btn-indent', () => {
      sinkListItem(schema.nodes.list_item)(this.view.state, this.view.dispatch);
      this.view.focus();
    });
    this._btn('btn-outdent', () => {
      liftListItem(schema.nodes.list_item)(this.view.state, this.view.dispatch);
      this.view.focus();
    });

    // Insert table
    this._btn('btn-insert-table', () => this.insertTableCard(5, 5));

    // Insert image
    this._btn('btn-insert-image', () => {
      const src = prompt('Image URL:');
      if (src) {
        const node = schema.nodes.image.create({ src });
        const tr = this.view.state.tr.replaceSelectionWith(node);
        this.view.dispatch(tr);
      }
      this.view.focus();
    });

    // Insert horizontal rule
    this._btn('btn-insert-hr', () => {
      const hr = schema.nodes.horizontal_rule.create();
      const { state } = this.view;
      const { $from } = state.selection;
      const tr = state.tr.insert($from.after(), hr);
      this.view.dispatch(tr);
      this.view.focus();
    });

    // Clear formatting
    this._btn('btn-clear-format', () => {
      const { state } = this.view;
      const { from, to } = state.selection;
      let tr = state.tr;
      Object.keys(schema.marks).forEach(markName => {
        tr = tr.removeMark(from, to, schema.marks[markName]);
      });
      this.view.dispatch(tr);
      this.view.focus();
    });

    // Format Painter
    this._copiedMarks = null;
    this._formatPainterActive = false;
    this._btn('btn-format-painter', () => {
      const { state } = this.view;
      const { $from } = state.selection;
      this._copiedMarks = $from.marks().map(m => m.toJSON());
      this._formatPainterActive = true;
      const btn = document.getElementById('btn-format-painter');
      if (btn) btn.classList.add('active');
    });

    // Grammar Check (LanguageTool free API)
    this._btn('btn-grammar', () => this._runGrammarCheck());

    // Paper Templates
    this._btn('btn-templates', () => this._showTemplateMenu());

    // Print
    this._btn('btn-print', () => this._print());

    // Export — Send to PDF
    this._btn('btn-export', () => this._showExportMenu());

    // Page Break
    this._btn('btn-page-break', () => this._insertPageBreak());

    // Table of Contents
    this._btn('btn-toc', () => this._insertTableOfContents());

    // Columns
    const colSel = document.getElementById('sel-columns');
    if (colSel) {
      colSel.addEventListener('change', () => {
        this._setColumns(parseInt(colSel.value));
        this.view.focus();
      });
    }

    // Page Setup
    this._btn('btn-page-setup', () => this._showPageSetup());

    // Header/Footer toggle
    this._btn('btn-header-footer', () => this._toggleHeaderFooter());

    // Line Spacing
    const lsSel = document.getElementById('sel-line-spacing');
    if (lsSel) {
      lsSel.addEventListener('change', () => {
        const editor = document.getElementById('editor');
        if (editor) editor.style.lineHeight = lsSel.value;
        localStorage.setItem('of-line-spacing', lsSel.value);
        this.view.focus();
      });
      const savedLS = localStorage.getItem('of-line-spacing');
      if (savedLS) { lsSel.value = savedLS; const editor = document.getElementById('editor'); if (editor) editor.style.lineHeight = savedLS; }
    }

    // Word count click for detail dialog
    const wcEl = document.getElementById('status-words');
    if (wcEl) wcEl.addEventListener('click', () => this._showWordCountDialog());
  }

  // ==========================================
  // TOOLBAR HELPERS
  // ==========================================

  _btn(id, handler) {
    const el = document.getElementById(id);
    if (el) el.addEventListener('click', () => { handler(); this.view.focus(); });
  }

  _applyMark(markType, attrs) {
    const { state } = this.view;
    const { from, to } = state.selection;
    if (from === to) return; // no selection
    const tr = state.tr.addMark(from, to, markType.create(attrs));
    this.view.dispatch(tr);
  }

  _removeMark(markType) {
    const { state } = this.view;
    const { from, to } = state.selection;
    if (from === to) return;
    const tr = state.tr.removeMark(from, to, markType);
    this.view.dispatch(tr);
  }

  _setAlignment(align) {
    const { state } = this.view;
    const { $from, $to } = state.selection;
    let tr = state.tr;

    state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if (node.type === schema.nodes.paragraph || node.type === schema.nodes.heading) {
        tr = tr.setNodeMarkup(pos, null, { ...node.attrs, align });
      }
    });

    this.view.dispatch(tr);
    this.view.focus();
  }

  _wrapIn(nodeType) {
    const { state } = this.view;
    const { $from, $to } = state.selection;
    const range = $from.blockRange($to);
    if (!range) return;

    const wrapping = findWrapping(range, nodeType);
    if (wrapping) {
      const tr = state.tr.wrap(range, wrapping);
      this.view.dispatch(tr);
    }
  }

  // Update toolbar button states based on cursor position
  _updateToolbarState(state) {
    const { $from } = state.selection;

    // Update block type dropdown
    const blockSel = document.getElementById('sel-block-type');
    if (blockSel) {
      const parent = $from.parent;
      if (parent.type === schema.nodes.heading) {
        blockSel.value = 'h' + parent.attrs.level;
      } else if (parent.type === schema.nodes.code_block) {
        blockSel.value = 'code_block';
      } else {
        blockSel.value = 'paragraph';
      }
    }

    // Update mark button active states
    const marks = state.storedMarks || state.selection.$from.marks();
    const markNames = marks.map(m => m.type.name);

    this._setActive('btn-bold', markNames.includes('strong'));
    this._setActive('btn-italic', markNames.includes('em'));
    this._setActive('btn-underline', markNames.includes('underline'));
    this._setActive('btn-strike', markNames.includes('strikethrough'));
    this._setActive('btn-superscript', markNames.includes('superscript'));
    this._setActive('btn-subscript', markNames.includes('subscript'));

    // Update alignment buttons
    const parent = $from.parent;
    const align = parent.attrs.align || 'left';
    this._setActive('btn-align-left', align === 'left');
    this._setActive('btn-align-center', align === 'center');
    this._setActive('btn-align-right', align === 'right');
    this._setActive('btn-align-justify', align === 'justify');
  }

  _setActive(id, active) {
    const el = document.getElementById(id);
    if (el) el.classList.toggle('active', active);
  }

  // ==========================================
  // CARD SYNC
  // ==========================================

  _syncCards(state) {
    const existingIds = new Set();
    const doc = state.doc;

    doc.forEach((node, offset, index) => {
      let cardId = node.attrs.cardId;
      let type = 'text';
      let preview = '';

      if (node.type.name === 'heading') {
        type = 'heading';
        preview = node.textContent.substring(0, 80);
      } else if (node.type.name === 'paragraph') {
        type = 'text';
        preview = node.textContent.substring(0, 80);
      } else if (node.type.name === 'table_card') {
        type = 'table';
        preview = `${node.attrs.rows}×${node.attrs.cols} table`;
        cardId = node.attrs.cardId;
      } else if (node.type.name === 'bullet_list' || node.type.name === 'ordered_list') {
        type = 'text';
        preview = 'List: ' + node.textContent.substring(0, 60);
      } else if (node.type.name === 'blockquote') {
        type = 'text';
        preview = 'Quote: ' + node.textContent.substring(0, 60);
      } else if (node.type.name === 'code_block') {
        type = 'text';
        preview = 'Code: ' + node.textContent.substring(0, 60);
      } else if (node.type.name === 'horizontal_rule') {
        return; // Don't card-ify HRs
      }

      if (cardId) {
        existingIds.add(cardId);
        const card = this.cardEngine.getCard(cardId);
        if (card) {
          this.cardEngine.updateCard(cardId, { preview });
        }
      } else if (node.attrs && 'cardId' in node.attrs) {
        const card = this.cardEngine.createCard(type, { preview });
        existingIds.add(card.id);
        const pos = this._getNodePos(state, index);
        if (pos !== null) {
          try {
            const tr = this.view.state.tr.setNodeMarkup(pos, null, { ...node.attrs, cardId: card.id });
            this.view.dispatch(tr);
          } catch (e) {
            // Node may have changed, safe to skip
          }
        }
      }
    });

    // Remove orphaned cards
    this.cardEngine.getAllCards().forEach(card => {
      if (!existingIds.has(card.id)) {
        this.cardEngine.removeCard(card.id);
      }
    });
  }

  _getNodePos(state, targetIndex) {
    let index = 0;
    let found = null;
    state.doc.forEach((node, offset) => {
      if (index === targetIndex && found === null) {
        found = offset;
      }
      index++;
    });
    return found;
  }

  // Insert a table card
  insertTableCard(rows = 4, cols = 4) {
    const card = this.cardEngine.createCard('table', {
      preview: `${rows}×${cols} table`,
      data: { cells: {} }
    });

    const tableNode = schema.nodes.table_card.create({
      cardId: card.id,
      name: card.name,
      rows,
      cols,
      data: { cells: {} }
    });

    const { state } = this.view;
    const { $from } = state.selection;
    const insertPos = $from.after();
    const tr = state.tr.insert(insertPos, tableNode);
    this.view.dispatch(tr);
    this.view.focus();
  }

  // ==========================================
  // GRAMMAR CHECK (LanguageTool API)
  // ==========================================

  async _runGrammarCheck() {
    const text = this.view.state.doc.textContent;
    if (!text.trim()) { alert('Document is empty — nothing to check.'); return; }

    const btn = document.getElementById('btn-grammar');
    if (btn) btn.textContent = '⏳ Checking...';

    try {
      const resp = await fetch('https://api.languagetool.org/v2/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `text=${encodeURIComponent(text)}&language=en-US&enabledCategories=GRAMMAR,SPELLING,PUNCTUATION,TYPOGRAPHY,CASING,REDUNDANCY,STYLE,TYPOS&enabledOnly=false`
      });
      const data = await resp.json();

      if (!data.matches || data.matches.length === 0) {
        alert('No grammar, spelling, or punctuation issues found!');
      } else {
        this._showGrammarResults(data.matches);
      }
    } catch (e) {
      alert('Grammar check failed — check your internet connection.');
    }

    if (btn) btn.innerHTML = '&#9998; Grammar';
  }

  _showGrammarResults(matches) {
    // Remove any existing grammar panel
    const existing = document.getElementById('grammar-panel');
    if (existing) existing.remove();

    const panel = document.createElement('div');
    panel.id = 'grammar-panel';
    panel.style.cssText = `
      position: fixed; top: 80px; right: 20px; width: 340px; max-height: 70vh;
      background: white; border: 1px solid #ddd; border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.18); z-index: 200;
      display: flex; flex-direction: column; font-family: inherit;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      padding: 12px 16px; border-bottom: 1px solid #eee; display: flex;
      justify-content: space-between; align-items: center;
    `;
    header.innerHTML = `<strong style="color:#2d2d2d">${matches.length} issue${matches.length > 1 ? 's' : ''} found</strong>`;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'background:none;border:none;font-size:18px;cursor:pointer;color:#999;';
    closeBtn.onclick = () => panel.remove();
    header.appendChild(closeBtn);
    panel.appendChild(header);

    const list = document.createElement('div');
    list.style.cssText = 'overflow-y: auto; flex: 1; padding: 8px;';

    matches.forEach((m, i) => {
      const item = document.createElement('div');
      item.style.cssText = `
        padding: 10px 12px; margin-bottom: 6px; border: 1px solid #f0f0f0;
        border-radius: 6px; cursor: pointer; transition: background 0.1s;
        border-left: 3px solid ${m.rule.issueType === 'misspelling' ? '#e94560' : '#f39c12'};
      `;
      item.onmouseenter = () => item.style.background = '#f8f8ff';
      item.onmouseleave = () => item.style.background = '';

      const context = m.context.text;
      const errStart = m.context.offset;
      const errEnd = errStart + m.context.length;
      const highlighted = context.substring(0, errStart) +
        '<span style="background:#ffe0e0;color:#c00;font-weight:600">' +
        context.substring(errStart, errEnd) + '</span>' +
        context.substring(errEnd);

      const suggestions = (m.replacements || []).slice(0, 3).map(r => r.value);
      const sugHtml = suggestions.length > 0
        ? suggestions.map(s =>
            `<button class="grammar-fix-btn" data-offset="${m.offset}" data-length="${m.length}" data-fix="${s}" style="background:#e8f5e9;border:1px solid #a5d6a7;border-radius:3px;padding:2px 8px;font-size:11px;cursor:pointer;color:#2e7d32;margin-right:4px">${s}</button>`
          ).join('')
        : '<span style="color:#999;font-size:11px">No suggestions</span>';

      item.innerHTML = `
        <div style="font-size:12px;color:#666;margin-bottom:4px">${m.message}</div>
        <div style="font-size:12px;color:#333;margin-bottom:6px;line-height:1.5">...${highlighted}...</div>
        <div>${sugHtml}</div>
      `;
      list.appendChild(item);
    });

    panel.appendChild(list);
    document.body.appendChild(panel);

    // Wire up fix buttons
    panel.querySelectorAll('.grammar-fix-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const offset = parseInt(btn.dataset.offset);
        const length = parseInt(btn.dataset.length);
        const fix = btn.dataset.fix;
        this._applyGrammarFix(offset, length, fix);
        btn.closest('div[style]').style.opacity = '0.4';
        btn.closest('div[style]').style.pointerEvents = 'none';
      });
    });
  }

  _applyGrammarFix(textOffset, length, replacement) {
    // Find the position in ProseMirror doc that corresponds to the text offset
    const { doc } = this.view.state;
    let charCount = 0;
    let from = null, to = null;

    doc.descendants((node, pos) => {
      if (from !== null) return false;
      if (node.isText) {
        const start = charCount;
        const end = charCount + node.text.length;
        if (textOffset >= start && textOffset < end) {
          from = pos + (textOffset - start);
          to = from + length;
        }
        charCount = end;
      }
    });

    if (from !== null && to !== null) {
      const tr = this.view.state.tr.replaceWith(from, to, schema.text(replacement));
      this.view.dispatch(tr);
    }
  }

  // ==========================================
  // PAPER TEMPLATES
  // ==========================================

  _showTemplateMenu() {
    const existing = document.getElementById('template-panel');
    if (existing) { existing.remove(); return; }

    const templates = [
      { name: 'APA Research Paper', id: 'apa', desc: 'Title page, abstract, body, references' },
      { name: 'MLA Essay', id: 'mla', desc: 'Header, body with citations, works cited' },
      { name: 'Business Letter', id: 'business-letter', desc: 'Date, address, salutation, body, closing' },
      { name: 'Meeting Notes', id: 'meeting-notes', desc: 'Date, attendees, agenda, action items' },
      { name: 'Project Proposal', id: 'proposal', desc: 'Executive summary, scope, timeline, budget' },
      { name: 'Resume / CV', id: 'resume', desc: 'Contact, summary, experience, education, skills' },
      { name: 'Invoice', id: 'invoice', desc: 'Company info, line items, totals, terms' },
      { name: 'Standard Report', id: 'report', desc: 'Title, executive summary, findings, conclusion' },
      { name: 'Blank Document', id: 'blank', desc: 'Start fresh with no template' },
    ];

    const panel = document.createElement('div');
    panel.id = 'template-panel';
    panel.style.cssText = `
      position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%);
      width: 480px; max-width: 90vw; max-height: 80vh; background: white;
      border-radius: 12px; box-shadow: 0 16px 48px rgba(0,0,0,0.25);
      z-index: 300; display: flex; flex-direction: column; font-family: inherit;
    `;

    const header = document.createElement('div');
    header.style.cssText = 'padding:16px 20px;border-bottom:1px solid #eee;display:flex;justify-content:space-between;align-items:center;';
    const titleArea = document.createElement('div');
    titleArea.style.cssText = 'display:flex;gap:0;';

    const tabPage = document.createElement('button');
    tabPage.textContent = 'Page Layouts';
    tabPage.style.cssText = 'padding:6px 14px;border:1px solid #ddd;border-bottom:2px solid #e94560;background:#fff;font-size:13px;font-weight:600;color:#e94560;cursor:pointer;border-radius:6px 6px 0 0;';

    const tabCard = document.createElement('button');
    tabCard.textContent = 'Card Templates';
    tabCard.style.cssText = 'padding:6px 14px;border:1px solid #ddd;border-bottom:2px solid transparent;background:#f5f5f5;font-size:13px;font-weight:500;color:#888;cursor:pointer;border-radius:6px 6px 0 0;';

    titleArea.appendChild(tabPage);
    titleArea.appendChild(tabCard);
    header.appendChild(titleArea);

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'background:none;border:none;font-size:18px;cursor:pointer;color:#999;';
    closeBtn.onclick = () => panel.remove();
    header.appendChild(closeBtn);
    panel.appendChild(header);

    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;flex:1;padding:12px;';

    const renderPageTemplates = () => {
      list.innerHTML = '';
      templates.forEach(t => {
        const item = document.createElement('div');
        item.style.cssText = `
          padding:12px 16px;margin-bottom:8px;border:1px solid #e8e8e8;
          border-radius:8px;cursor:pointer;transition:all 0.15s;
        `;
        item.onmouseenter = () => { item.style.borderColor = '#e94560'; item.style.background = '#fdf2f4'; };
        item.onmouseleave = () => { item.style.borderColor = '#e8e8e8'; item.style.background = ''; };
        item.innerHTML = `
          <div style="font-weight:600;font-size:14px;color:#2d2d2d;margin-bottom:2px">${t.name}</div>
          <div style="font-size:12px;color:#888">${t.desc}</div>
        `;
        item.onclick = () => { this._applyTemplate(t.id); panel.remove(); };
        list.appendChild(item);
      });
    };

    const renderCardTemplates = () => {
      list.innerHTML = '';
      const presets = this.cardEngine.getTemplatePresets();
      const typeIcons = { 'heading': 'H', 'snippet': 'T', 'date-stamp': 'D', 'signature': 'S', 'table': '#', 'calculation': 'f' };

      presets.forEach(preset => {
        const item = document.createElement('div');
        item.style.cssText = `
          padding:12px 16px;margin-bottom:8px;border:1px solid #e8e8e8;
          border-radius:8px;cursor:pointer;transition:all 0.15s;
        `;
        item.onmouseenter = () => { item.style.borderColor = '#e94560'; item.style.background = '#fdf2f4'; };
        item.onmouseleave = () => { item.style.borderColor = '#e8e8e8'; item.style.background = ''; };

        const slotPreview = preset.slots.slice(0, 5).map(s =>
          `<span style="display:inline-block;background:#f0f0f0;border-radius:4px;padding:1px 6px;font-size:11px;margin:1px 2px;color:#555">${typeIcons[s.type] || '?'} ${s.name}</span>`
        ).join('');
        const more = preset.slots.length > 5 ? `<span style="font-size:11px;color:#999"> +${preset.slots.length - 5} more</span>` : '';

        item.innerHTML = `
          <div style="font-weight:600;font-size:14px;color:#2d2d2d;margin-bottom:2px">${preset.name}</div>
          <div style="font-size:12px;color:#888;margin-bottom:4px">${preset.description}</div>
          <div style="margin-top:4px">${slotPreview}${more}</div>
          <div style="margin-top:4px">${preset.tags.map(t => `<span style="font-size:10px;color:#e94560;margin-right:6px">#${t}</span>`).join('')}</div>
        `;
        item.onclick = () => { this._applyCardTemplate(preset); panel.remove(); };
        list.appendChild(item);
      });

      // Install All button
      const installBtn = document.createElement('div');
      installBtn.style.cssText = 'padding:10px 16px;margin-top:8px;text-align:center;border:1px dashed #ccc;border-radius:8px;cursor:pointer;color:#888;font-size:13px;';
      installBtn.textContent = 'Save All to Card Library';
      installBtn.onmouseenter = () => { installBtn.style.borderColor = '#e94560'; installBtn.style.color = '#e94560'; };
      installBtn.onmouseleave = () => { installBtn.style.borderColor = '#ccc'; installBtn.style.color = '#888'; };
      installBtn.onclick = () => {
        const installed = this.cardEngine.installAllTemplates();
        installBtn.textContent = `${installed.length} templates saved to library`;
        installBtn.style.color = '#28a745';
        installBtn.style.borderColor = '#28a745';
        installBtn.style.cursor = 'default';
      };
      list.appendChild(installBtn);
    };

    tabPage.onclick = () => {
      tabPage.style.borderBottom = '2px solid #e94560'; tabPage.style.color = '#e94560'; tabPage.style.fontWeight = '600'; tabPage.style.background = '#fff';
      tabCard.style.borderBottom = '2px solid transparent'; tabCard.style.color = '#888'; tabCard.style.fontWeight = '500'; tabCard.style.background = '#f5f5f5';
      renderPageTemplates();
    };
    tabCard.onclick = () => {
      tabCard.style.borderBottom = '2px solid #e94560'; tabCard.style.color = '#e94560'; tabCard.style.fontWeight = '600'; tabCard.style.background = '#fff';
      tabPage.style.borderBottom = '2px solid transparent'; tabPage.style.color = '#888'; tabPage.style.fontWeight = '500'; tabPage.style.background = '#f5f5f5';
      renderCardTemplates();
    };

    renderPageTemplates();
    panel.appendChild(list);
    document.body.appendChild(panel);
  }

  _applyTemplate(id) {
    const nodes = [];
    const h = (level, text) => schema.nodes.heading.create({ level }, [schema.text(text)]);
    const p = (text) => text ? schema.nodes.paragraph.create({}, [schema.text(text)]) : schema.nodes.paragraph.create({}, []);
    const pAlign = (text, align) => schema.nodes.paragraph.create({ align }, text ? [schema.text(text)] : []);

    const templates = {
      'apa': () => [
        pAlign('', 'center'), pAlign('', 'center'), pAlign('', 'center'),
        pAlign('Your Paper Title', 'center'),
        pAlign('Your Name', 'center'),
        pAlign('Institution Name', 'center'),
        pAlign('Course Name', 'center'),
        pAlign('Instructor Name', 'center'),
        pAlign('Date', 'center'),
        schema.nodes.horizontal_rule.create(),
        h(1, 'Abstract'),
        p('Write a 150-250 word summary of your paper here. The abstract should briefly describe the topic, methodology, results, and conclusions.'),
        p('Keywords: keyword1, keyword2, keyword3'),
        schema.nodes.horizontal_rule.create(),
        h(1, 'Introduction'),
        p('Introduce your topic and state your thesis or research question. Provide background context and explain why this research matters.'),
        h(1, 'Literature Review'),
        p('Summarize and analyze existing research related to your topic. Identify gaps in the current literature.'),
        h(1, 'Methodology'),
        p('Describe your research methods, data collection, and analysis approach.'),
        h(1, 'Results'),
        p('Present your findings using clear language. Reference any tables or figures.'),
        h(1, 'Discussion'),
        p('Interpret your results. Discuss implications, limitations, and suggestions for future research.'),
        h(1, 'Conclusion'),
        p('Summarize your key findings and their significance.'),
        h(1, 'References'),
        p('Author, A. A. (Year). Title of work. Publisher. https://doi.org/xxxxx'),
      ],
      'mla': () => [
        p('Your Name'),
        p('Instructor Name'),
        p('Course Name'),
        p('Date'),
        pAlign('Your Paper Title', 'center'),
        p('Begin your essay here. In MLA format, the first line of each paragraph is indented 0.5 inches. Use 12pt Times New Roman, double-spaced throughout.'),
        p('Continue your argument with supporting evidence. Use in-text citations like this (Author 45). Each body paragraph should have a clear topic sentence, evidence, and analysis.'),
        p('Your conclusion should restate your thesis and summarize your main points without introducing new arguments.'),
        schema.nodes.horizontal_rule.create(),
        pAlign('Works Cited', 'center'),
        p('Last, First. "Article Title." Journal Name, vol. #, no. #, Year, pp. #-#.'),
        p('Last, First. Book Title. Publisher, Year.'),
      ],
      'business-letter': () => [
        p('Your Company Name'),
        p('Your Address'),
        p('City, State ZIP'),
        p(''),
        p(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })),
        p(''),
        p('Recipient Name'),
        p('Recipient Title'),
        p('Company Name'),
        p('Address'),
        p('City, State ZIP'),
        p(''),
        p('Dear [Recipient Name],'),
        p(''),
        p('I am writing to [state the purpose of your letter]. This opening paragraph should be direct and clearly explain why you are writing.'),
        p('In this paragraph, provide supporting details, context, or additional information relevant to your purpose. Be specific and concise.'),
        p('In your closing paragraph, summarize your request or next steps. Include any deadlines or follow-up actions needed.'),
        p(''),
        p('Sincerely,'),
        p(''),
        p(''),
        p('Your Name'),
        p('Your Title'),
        p('Phone: (000) 000-0000'),
        p('Email: you@company.com'),
      ],
      'meeting-notes': () => [
        h(1, 'Meeting Notes'),
        p(`Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`),
        p('Time: '),
        p('Location: '),
        p(''),
        h(2, 'Attendees'),
        p('- Name (Role)'),
        p('- Name (Role)'),
        p(''),
        h(2, 'Agenda'),
        p('1. '),
        p('2. '),
        p('3. '),
        p(''),
        h(2, 'Discussion Notes'),
        p(''),
        h(2, 'Decisions Made'),
        p('- '),
        p(''),
        h(2, 'Action Items'),
        p('- [ ] Task — Owner — Due Date'),
        p('- [ ] Task — Owner — Due Date'),
        p(''),
        h(2, 'Next Meeting'),
        p('Date: '),
      ],
      'proposal': () => [
        pAlign('Project Proposal', 'center'),
        pAlign('Prepared by: Your Name', 'center'),
        pAlign(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), 'center'),
        schema.nodes.horizontal_rule.create(),
        h(1, 'Executive Summary'),
        p('Briefly describe the project, its objectives, and the expected outcomes. This section should be 1-2 paragraphs maximum.'),
        h(1, 'Problem Statement'),
        p('Clearly define the problem or opportunity this project addresses.'),
        h(1, 'Proposed Solution'),
        p('Describe your approach and how it solves the problem. Include key features and deliverables.'),
        h(1, 'Scope'),
        h(2, 'In Scope'),
        p('- '),
        h(2, 'Out of Scope'),
        p('- '),
        h(1, 'Timeline'),
        p('Phase 1: '),
        p('Phase 2: '),
        p('Phase 3: '),
        h(1, 'Budget'),
        p('Provide a cost breakdown for the project.'),
        h(1, 'Team & Resources'),
        p('List the team members and resources required.'),
        h(1, 'Risks & Mitigation'),
        p('Identify potential risks and your plan to address them.'),
        h(1, 'Success Criteria'),
        p('Define how success will be measured.'),
      ],
      'resume': () => [
        pAlign('YOUR FULL NAME', 'center'),
        pAlign('City, State | (000) 000-0000 | email@example.com | linkedin.com/in/yourname', 'center'),
        schema.nodes.horizontal_rule.create(),
        h(2, 'Professional Summary'),
        p('Results-driven professional with X years of experience in [field]. Skilled in [key skills]. Proven track record of [key achievement].'),
        schema.nodes.horizontal_rule.create(),
        h(2, 'Experience'),
        h(3, 'Job Title — Company Name'),
        p('Location | Start Date – Present'),
        p('- Led [initiative] resulting in [measurable outcome]'),
        p('- Managed [team/project] and delivered [result]'),
        p('- Developed [system/process] that improved [metric] by X%'),
        p(''),
        h(3, 'Previous Job Title — Company Name'),
        p('Location | Start Date – End Date'),
        p('- Accomplishment with measurable result'),
        p('- Accomplishment with measurable result'),
        schema.nodes.horizontal_rule.create(),
        h(2, 'Education'),
        h(3, 'Degree — University Name'),
        p('Graduation Year | GPA (if notable)'),
        schema.nodes.horizontal_rule.create(),
        h(2, 'Skills'),
        p('Skill Category: Skill 1, Skill 2, Skill 3'),
        p('Skill Category: Skill 1, Skill 2, Skill 3'),
      ],
      'invoice': () => [
        h(1, 'INVOICE'),
        p(''),
        p('Invoice #: INV-001'),
        p(`Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`),
        p('Due Date: '),
        p(''),
        h(2, 'From'),
        p('Your Company Name'),
        p('Your Address'),
        p('City, State ZIP'),
        p('Phone: (000) 000-0000'),
        p(''),
        h(2, 'Bill To'),
        p('Client Name'),
        p('Client Company'),
        p('Client Address'),
        p('City, State ZIP'),
        schema.nodes.horizontal_rule.create(),
        h(2, 'Items'),
        p('Description | Qty | Rate | Amount'),
        p('Service/Product 1 | 1 | $0.00 | $0.00'),
        p('Service/Product 2 | 1 | $0.00 | $0.00'),
        schema.nodes.horizontal_rule.create(),
        pAlign('Subtotal: $0.00', 'right'),
        pAlign('Tax (0%): $0.00', 'right'),
        pAlign('Total: $0.00', 'right'),
        p(''),
        h(2, 'Payment Terms'),
        p('Payment is due within 30 days. Please make checks payable to Your Company Name.'),
        p('Bank: | Account: | Routing: '),
      ],
      'report': () => [
        pAlign('Report Title', 'center'),
        pAlign('Prepared by: Your Name', 'center'),
        pAlign('Department / Organization', 'center'),
        pAlign(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), 'center'),
        schema.nodes.horizontal_rule.create(),
        h(1, 'Executive Summary'),
        p('Provide a high-level overview of the report\'s purpose, key findings, and recommendations. This should be readable as a standalone summary.'),
        h(1, 'Introduction'),
        p('State the purpose of the report, the scope, and any background information the reader needs.'),
        h(1, 'Methodology'),
        p('Describe how data was collected and analyzed.'),
        h(1, 'Findings'),
        h(2, 'Finding 1'),
        p('Detail your first major finding with supporting data.'),
        h(2, 'Finding 2'),
        p('Detail your second major finding with supporting data.'),
        h(1, 'Analysis'),
        p('Interpret the findings. What do they mean? What patterns or trends emerge?'),
        h(1, 'Recommendations'),
        p('1. First recommendation based on findings'),
        p('2. Second recommendation'),
        p('3. Third recommendation'),
        h(1, 'Conclusion'),
        p('Summarize the key points and restate the most important recommendations.'),
        h(1, 'Appendix'),
        p('Include any supplementary data, charts, or detailed tables here.'),
      ],
      'blank': () => [p('')],
    };

    const builder = templates[id];
    if (!builder) return;

    const content = builder();
    const doc = schema.node('doc', null, content);
    const newState = EditorState.create({
      doc,
      plugins: this.view.state.plugins
    });
    this.view.updateState(newState);
    this._syncCards(this.view.state);
  }

  focus() {
    this.view.focus();
  }

  // ==========================================
  // AUTO-SAVE (localStorage)
  // ==========================================

  _scheduleAutoSave() {
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = setTimeout(() => this._saveToStorage(), 1500);
    const ind = document.getElementById('save-indicator');
    if (ind) { ind.textContent = '○ Unsaved'; ind.style.color = '#f59e0b'; }
  }

  _saveToStorage() {
    try {
      const json = this.view.state.doc.toJSON();
      localStorage.setItem('of-doc', JSON.stringify(json));
      const titleEl = document.getElementById('doc-title');
      if (titleEl) localStorage.setItem('of-doc-title', titleEl.textContent);
      // Save header/footer
      const hdr = document.getElementById('page-header');
      const ftr = document.getElementById('page-footer');
      if (hdr) localStorage.setItem('of-page-header', hdr.innerHTML);
      if (ftr) localStorage.setItem('of-page-footer', ftr.innerHTML);
      const ind = document.getElementById('save-indicator');
      if (ind) { ind.textContent = '● Saved'; ind.style.color = '#4ade80'; }
    } catch (e) { /* storage full or error */ }
  }

  newDocument() {
    const emptyDoc = schema.node('doc', null, [
      schema.node('paragraph', { cardId: null }, [])
    ]);
    const newState = EditorState.create({ doc: emptyDoc, plugins: this.view.state.plugins });
    this.view.updateState(newState);
    this.cardEngine.clear();
    this._syncCards(this.view.state);
    localStorage.removeItem('of-doc');
    localStorage.removeItem('of-doc-title');
    localStorage.removeItem('of-page-header');
    localStorage.removeItem('of-page-footer');
    const titleEl = document.getElementById('doc-title');
    if (titleEl) titleEl.textContent = 'Untitled Document';
    const ind = document.getElementById('save-indicator');
    if (ind) { ind.textContent = '● Saved'; ind.style.color = '#4ade80'; }
    this.view.focus();
  }

  _loadSavedDoc() {
    try {
      const saved = localStorage.getItem('of-doc');
      if (!saved) return;
      const json = JSON.parse(saved);
      const doc = schema.nodeFromJSON(json);
      const newState = EditorState.create({ doc, plugins: this.view.state.plugins });
      this.view.updateState(newState);
      this._syncCards(this.view.state);
      const title = localStorage.getItem('of-doc-title');
      if (title) { const el = document.getElementById('doc-title'); if (el) el.textContent = title; }
      // Restore header/footer
      const hdr = document.getElementById('page-header');
      const ftr = document.getElementById('page-footer');
      const savedHdr = localStorage.getItem('of-page-header');
      const savedFtr = localStorage.getItem('of-page-footer');
      if (hdr && savedHdr) { hdr.innerHTML = savedHdr; hdr.style.display = ''; }
      if (ftr && savedFtr) { ftr.innerHTML = savedFtr; ftr.style.display = ''; }
    } catch (e) { /* corrupt save, ignore */ }
  }

  // ==========================================
  // CARD TEMPLATES → DOCUMENT
  // ==========================================

  _applyCardTemplate(preset) {
    if (!this.view) return;

    // Install into library if not already there
    this.cardEngine.installTemplate(preset.name);

    const nodes = [];
    const h = (level, text) => schema.nodes.heading.create({ level }, [schema.text(text)]);
    const p = (text) => text ? schema.nodes.paragraph.create({}, [schema.text(text)]) : schema.nodes.paragraph.create({}, []);

    preset.slots.forEach(slot => {
      if (slot.type === 'heading') {
        const level = (slot.data && slot.data.level) || 1;
        nodes.push(h(level, slot.data?.content || slot.name));

      } else if (slot.type === 'snippet' || slot.type === 'text') {
        const content = slot.data?.content || slot.placeholder || '';
        // Split multi-line snippets into separate paragraphs
        content.split('\n').forEach(line => {
          nodes.push(p(line || ''));
        });

      } else if (slot.type === 'date-stamp') {
        const format = slot.data?.format || 'short';
        const opts = format === 'long'
          ? { year: 'numeric', month: 'long', day: 'numeric' }
          : { year: 'numeric', month: '2-digit', day: '2-digit' };
        const dateStr = new Date().toLocaleDateString('en-US', opts);
        nodes.push(p(dateStr));

      } else if (slot.type === 'signature') {
        nodes.push(p(`[${slot.name || 'Signature'}: ___________________]`));
        nodes.push(p(''));

      } else if (slot.type === 'table') {
        // Render table as formatted text rows (ProseMirror table nodes are complex)
        if (slot.data?.headers) {
          nodes.push(p(slot.data.headers.join('  |  ')));
          nodes.push(p(slot.data.headers.map(() => '---').join('  |  ')));
          if (slot.data.rows) {
            slot.data.rows.forEach(row => {
              nodes.push(p(row.join('  |  ')));
            });
          }
        }

      } else if (slot.type === 'calculation') {
        nodes.push(p(`{${slot.name}}`));

      } else {
        nodes.push(p(slot.placeholder || slot.name));
      }
    });

    if (nodes.length === 0) return;

    // Replace document content with the template
    const tr = this.view.state.tr;
    tr.replaceWith(0, this.view.state.doc.content.size, nodes);
    this.view.dispatch(tr);
    this.view.focus();
  }

  // ==========================================
  // PRINT
  // ==========================================

  _print() { window.print(); }

  // ==========================================
  // PAGE BREAK
  // ==========================================

  _insertPageBreak() {
    const { state } = this.view;
    const { $from } = state.selection;
    const pb = schema.nodes.page_break.create();
    const tr = state.tr.insert($from.after(), pb);
    this.view.dispatch(tr);
    this.view.focus();
  }

  // ==========================================
  // TABLE OF CONTENTS
  // ==========================================

  _insertTableOfContents() {
    const headings = [];
    this.view.state.doc.forEach((node) => {
      if (node.type === schema.nodes.heading) {
        headings.push({ level: node.attrs.level, text: node.textContent });
      }
    });
    if (headings.length === 0) { alert('No headings found. Add headings first.'); return; }

    const nodes = [];
    nodes.push(schema.nodes.heading.create({ level: 1 }, [schema.text('Table of Contents')]));
    nodes.push(schema.nodes.horizontal_rule.create());
    headings.forEach(h => {
      const prefix = h.level === 1 ? '' : h.level === 2 ? '    ' : '        ';
      nodes.push(schema.nodes.paragraph.create({}, [schema.text(`${prefix}${h.text}`)]));
    });
    nodes.push(schema.nodes.horizontal_rule.create());

    const { $from } = this.view.state.selection;
    let tr = this.view.state.tr;
    let pos = $from.start(1);
    nodes.forEach(n => {
      tr = tr.insert(pos, n);
      pos += n.nodeSize;
    });
    this.view.dispatch(tr);
    this.view.focus();
  }

  // ==========================================
  // COLUMNS
  // ==========================================

  _setColumns(count) {
    const editor = document.getElementById('editor');
    if (!editor) return;
    if (count <= 1) {
      editor.style.columnCount = '';
      editor.style.columnGap = '';
      editor.style.columnRule = '';
    } else {
      editor.style.columnCount = count;
      editor.style.columnGap = '2em';
      editor.style.columnRule = '1px solid #e5e7eb';
    }
  }

  // ==========================================
  // WORD COUNT DIALOG
  // ==========================================

  _showWordCountDialog() {
    const existing = document.getElementById('wordcount-panel');
    if (existing) { existing.remove(); return; }

    const text = this.view.state.doc.textContent;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, '').length;
    const paragraphs = this.view.state.doc.childCount;
    const sentences = text.split(/[.!?]+/).filter(s => s.trim()).length;
    const readingTime = Math.max(1, Math.ceil(words / 200));
    const speakingTime = Math.max(1, Math.ceil(words / 130));

    const panel = document.createElement('div');
    panel.id = 'wordcount-panel';
    panel.style.cssText = `
      position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
      width:320px;background:white;border-radius:12px;
      box-shadow:0 16px 48px rgba(0,0,0,0.25);z-index:300;font-family:inherit;padding:24px;
    `;
    const row = (label, val) => `<tr style="border-bottom:1px solid #f0f0f0"><td style="padding:8px 0;color:#666">${label}</td><td style="text-align:right;font-weight:600">${val}</td></tr>`;
    panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <strong style="font-size:16px;color:#2d2d2d">Document Statistics</strong>
        <button onclick="this.closest('#wordcount-panel').remove()" style="background:none;border:none;font-size:18px;cursor:pointer;color:#999">\u2715</button>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:14px">
        ${row('Words', words.toLocaleString())}
        ${row('Characters', chars.toLocaleString())}
        ${row('Characters (no spaces)', charsNoSpaces.toLocaleString())}
        ${row('Paragraphs', paragraphs)}
        ${row('Sentences', sentences)}
        ${row('Reading time', `~${readingTime} min`)}
        <tr><td style="padding:8px 0;color:#666">Speaking time</td><td style="text-align:right;font-weight:600">~${speakingTime} min</td></tr>
      </table>
    `;
    document.body.appendChild(panel);
  }

  // ==========================================
  // PAGE SETUP
  // ==========================================

  _showPageSetup() {
    const existing = document.getElementById('page-setup-panel');
    if (existing) { existing.remove(); return; }

    const page = document.getElementById('editor-page');
    const currentSize = localStorage.getItem('of-page-size') || 'letter';
    const currentMargins = localStorage.getItem('of-page-margins') || 'normal';

    const panel = document.createElement('div');
    panel.id = 'page-setup-panel';
    panel.style.cssText = `
      position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
      width:360px;background:white;border-radius:12px;
      box-shadow:0 16px 48px rgba(0,0,0,0.25);z-index:300;font-family:inherit;padding:24px;
    `;
    const opt = (val, label, cur) => `<option value="${val}"${cur === val ? ' selected' : ''}>${label}</option>`;
    panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <strong style="font-size:16px;color:#2d2d2d">Page Setup</strong>
        <button onclick="this.closest('#page-setup-panel').remove()" style="background:none;border:none;font-size:18px;cursor:pointer;color:#999">\u2715</button>
      </div>
      <div style="margin-bottom:16px">
        <label style="font-size:13px;font-weight:600;color:#555;display:block;margin-bottom:4px">Paper Size</label>
        <select id="ps-size" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;font-size:14px">
          ${opt('letter', 'US Letter (8.5\u2033 \u00d7 11\u2033)', currentSize)}
          ${opt('a4', 'A4 (210mm \u00d7 297mm)', currentSize)}
          ${opt('legal', 'US Legal (8.5\u2033 \u00d7 14\u2033)', currentSize)}
          ${opt('a5', 'A5 (148mm \u00d7 210mm)', currentSize)}
        </select>
      </div>
      <div style="margin-bottom:16px">
        <label style="font-size:13px;font-weight:600;color:#555;display:block;margin-bottom:4px">Margins</label>
        <select id="ps-margins" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;font-size:14px">
          ${opt('normal', 'Normal (1\u2033)', currentMargins)}
          ${opt('narrow', 'Narrow (0.5\u2033)', currentMargins)}
          ${opt('wide', 'Wide (1.5\u2033)', currentMargins)}
          ${opt('academic', 'Academic (1\u2033 top/bottom, 1.25\u2033 sides)', currentMargins)}
        </select>
      </div>
      <button id="ps-apply" style="width:100%;padding:10px;background:linear-gradient(145deg,#1e40af,#1e3a8a);color:white;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer">Apply</button>
    `;
    document.body.appendChild(panel);
    document.getElementById('ps-apply').addEventListener('click', () => {
      this._applyPageSetup(document.getElementById('ps-size').value, document.getElementById('ps-margins').value);
      panel.remove();
    });
  }

  _applyPageSetup(size, margins) {
    const page = document.getElementById('editor-page');
    if (!page) return;

    const sizes = {
      letter: { width: '8.5in', minHeight: '11in' },
      a4: { width: '210mm', minHeight: '297mm' },
      legal: { width: '8.5in', minHeight: '14in' },
      a5: { width: '148mm', minHeight: '210mm' },
    };
    const marginValues = {
      normal: '96px',
      narrow: '48px',
      wide: '144px',
      academic: '96px 120px',
    };

    const s = sizes[size] || sizes.letter;
    const m = marginValues[margins] || marginValues.normal;
    page.style.maxWidth = s.width;
    page.style.minHeight = s.minHeight;
    page.style.padding = m;

    localStorage.setItem('of-page-size', size);
    localStorage.setItem('of-page-margins', margins);
  }

  _loadPageSetup() {
    const size = localStorage.getItem('of-page-size');
    const margins = localStorage.getItem('of-page-margins');
    if (size || margins) this._applyPageSetup(size || 'letter', margins || 'normal');
  }

  // ==========================================
  // HEADER & FOOTER
  // ==========================================

  _toggleHeaderFooter() {
    const hdr = document.getElementById('page-header');
    const ftr = document.getElementById('page-footer');
    if (!hdr || !ftr) return;
    const show = hdr.style.display === 'none';
    hdr.style.display = show ? '' : 'none';
    ftr.style.display = show ? '' : 'none';
    const btn = document.getElementById('btn-header-footer');
    if (btn) btn.classList.toggle('active', show);
  }

  // ==========================================
  // DRAG & DROP IMAGES
  // ==========================================

  _setupDragDrop() {
    const editorEl = document.getElementById('editor');
    if (!editorEl) return;

    editorEl.addEventListener('dragover', (e) => {
      e.preventDefault();
      editorEl.classList.add('drag-over');
    });
    editorEl.addEventListener('dragleave', () => {
      editorEl.classList.remove('drag-over');
    });
    editorEl.addEventListener('drop', (e) => {
      e.preventDefault();
      editorEl.classList.remove('drag-over');
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = () => {
            const node = schema.nodes.image.create({ src: reader.result, alt: file.name });
            const tr = this.view.state.tr.replaceSelectionWith(node);
            this.view.dispatch(tr);
          };
          reader.readAsDataURL(file);
        }
      }
    });

    // Also handle paste images
    editorEl.addEventListener('paste', (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          const reader = new FileReader();
          reader.onload = () => {
            const node = schema.nodes.image.create({ src: reader.result, alt: 'Pasted image' });
            const tr = this.view.state.tr.replaceSelectionWith(node);
            this.view.dispatch(tr);
          };
          reader.readAsDataURL(file);
        }
      }
    });
  }

  // ==========================================
  // @ CARD MENTION — Quick Insert
  // ==========================================

  _setupCardMention() {
    const editorEl = document.getElementById('editor');
    if (!editorEl) return;

    let mentionActive = false;
    let mentionStart = -1;
    let dropdown = null;

    const removeMentionUI = () => {
      mentionActive = false;
      mentionStart = -1;
      if (dropdown) { dropdown.remove(); dropdown = null; }
    };

    editorEl.addEventListener('keydown', (e) => {
      if (dropdown) {
        if (e.key === 'Escape') { removeMentionUI(); e.preventDefault(); return; }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const items = dropdown.querySelectorAll('.mention-item');
          let active = dropdown.querySelector('.mention-item.active');
          let idx = Array.from(items).indexOf(active);
          if (e.key === 'ArrowDown') idx = Math.min(idx + 1, items.length - 1);
          else idx = Math.max(idx - 1, 0);
          items.forEach(i => i.classList.remove('active'));
          if (items[idx]) { items[idx].classList.add('active'); items[idx].scrollIntoView({ block: 'nearest' }); }
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          const active = dropdown.querySelector('.mention-item.active');
          if (active) {
            e.preventDefault();
            const cardId = active.dataset.cardId;
            const card = this.cardEngine.getLibraryCard(cardId) || this.cardEngine.getCard(cardId);
            if (card) {
              if (card.type === 'lookup') {
                // Insert @cardname( and let user type the parameter
                const { state } = this.view;
                const { $from } = state.selection;
                const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\ufffc');
                const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9 ]*)$/);
                if (atMatch) {
                  const deleteFrom = $from.pos - atMatch[2].length - 1;
                  const tr = state.tr.replaceWith(deleteFrom, $from.pos, schema.text(`@${card.name}(`));
                  this.view.dispatch(tr);
                  this.view.focus();
                }
                removeMentionUI();
              } else {
                this._completeMention(card, mentionStart);
                removeMentionUI();
              }
            }
            return;
          }
        }
      }
    });

    // Detect @ typing via input events
    const self = this;
    const checkForMention = () => {
      if (!this.view) return;
      const { state } = this.view;
      const { $from } = state.selection;
      const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\ufffc');

      // 1. Check for @=cardname — insert OUTPUT/result node
      const outputMatch = textBefore.match(/(^|[^a-zA-Z0-9])@=([a-zA-Z0-9_\- ]+)$/);
      if (outputMatch) {
        const cardName = outputMatch[2].trim();
        const card = this._findCardByName(cardName);
        if (card && card.type === 'lookup') {
          const fullLength = outputMatch[0].length - outputMatch[1].length;
          this._insertOutputNode(card, $from.pos - fullLength, $from.pos);
          removeMentionUI();
          return;
        }
      }

      // 2. Check for completed @cardname(param) — insert INPUT/feed node
      const funcMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9_\- ]+)\(([^)]*)\)$/);
      if (funcMatch) {
        const cardName = funcMatch[2].trim();
        const param = funcMatch[3].trim();
        const fullLength = funcMatch[0].length - funcMatch[1].length;
        this._insertInputNode(cardName, param, $from.pos - fullLength, $from.pos);
        removeMentionUI();
        return;
      }

      // 3. Check for @cardname( in progress — show picker with values
      const openParenMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9_\- ]+)\(([^)]*)$/);
      if (openParenMatch) {
        const cardName = openParenMatch[2].trim();
        const partialParam = openParenMatch[3].trim();
        const card = this._findCardByName(cardName);
        if (card && card.type === 'lookup') {
          mentionActive = true;
          this._showParamPicker(card, partialParam, $from, removeMentionUI);
        }
        return;
      }

      // 4. Check for @=query — show card dropdown filtered for output placement
      const outputQueryMatch = textBefore.match(/(^|[^a-zA-Z0-9])@=([a-zA-Z0-9 ]*)$/);
      if (outputQueryMatch) {
        const query = outputQueryMatch[2];
        mentionStart = $from.pos - query.length - 2; // @= + query
        mentionActive = true;
        this._showMentionDropdown(query, $from, removeMentionUI, 'output');
        return;
      }

      // 5. Check for @query — show card name dropdown
      const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9 ]*)$/);
      if (atMatch) {
        const query = atMatch[2];
        mentionStart = $from.pos - query.length - 1; // position of @
        mentionActive = true;
        this._showMentionDropdown(query, $from, removeMentionUI);
      } else if (mentionActive) {
        removeMentionUI();
      }
    };

    // Hook into editor transactions
    const origDispatch = this.view.props.dispatchTransaction;
    // We'll poll on keyup instead to avoid breaking the dispatch chain
    editorEl.addEventListener('keyup', (e) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) return;
      checkForMention();
    });
  }

  _showMentionDropdown(query, $from, onClose, mode = 'input') {
    // Remove existing
    const existing = document.getElementById('card-mention-dropdown');
    if (existing) existing.remove();

    // Get matching cards from library + document
    const allCards = [
      ...this.cardEngine.getAllLibraryCards(),
      ...this.cardEngine.getAllCards(),
    ];

    const q = query.toLowerCase();
    const filtered = allCards.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.type.includes(q) ||
      c.tags.some(t => t.toLowerCase().includes(q))
    ).slice(0, 10);

    // Don't bail silently — always show the dropdown with feedback

    const dropdown = document.createElement('div');
    dropdown.id = 'card-mention-dropdown';
    dropdown.style.cssText = 'position:absolute;z-index:9000;background:#fff;border:1px solid #ddd;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.15);max-height:250px;overflow-y:auto;min-width:260px;';

    const typeIcons = {
      text: '\u00B6', heading: 'H', table: '\u2637', image: '\uD83D\uDDBC',
      snippet: '\u270E', spreadsheet: '\u2637', chart: '\uD83D\uDCC8',
      calculation: '\u0192', signature: '\u270D', 'date-stamp': '\uD83D\uDCC5',
      template: '\uD83D\uDCC4', lookup: '\uD83D\uDD0D',
    };

    if (filtered.length === 0) {
      if (allCards.length === 0) {
        dropdown.innerHTML = '<div style="padding:12px;font-size:12px;color:#999;line-height:1.5;">No cards yet.<br><strong style="color:#1e40af;">Go to Grid</strong>, select cells, and click <strong>Create Card</strong> button.</div>';
      } else {
        dropdown.innerHTML = `<div style="padding:12px;font-size:12px;color:#999;">No cards matching "${query}"</div>`;
      }
    } else {
      filtered.forEach((card, i) => {
        const item = document.createElement('div');
        item.className = 'mention-item' + (i === 0 ? ' active' : '');
        item.dataset.cardId = card.id;
        item.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:12px;display:flex;align-items:center;gap:8px;';
        item.innerHTML = `
          <span style="font-size:14px;">${typeIcons[card.type] || '\u2022'}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${card.name}</div>
            <div style="font-size:10px;color:#999;">${card.type}${card.tags.length ? ' \u2022 ' + card.tags.map(t => '#' + t).join(' ') : ''}</div>
          </div>
        `;
        item.addEventListener('mouseenter', () => {
          dropdown.querySelectorAll('.mention-item').forEach(i => i.classList.remove('active'));
          item.classList.add('active');
        });
        item.addEventListener('click', () => {
          if (mode === 'output' && card.type === 'lookup') {
            // Insert result node directly
            const { state } = this.view;
            const { $from } = state.selection;
            const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\ufffc');
            const eqMatch = textBefore.match(/(^|[^a-zA-Z0-9])@=([a-zA-Z0-9 ]*)$/);
            if (eqMatch) {
              const deleteFrom = $from.pos - eqMatch[2].length - 2; // @= + query
              this._insertOutputNode(card, deleteFrom, $from.pos);
            }
            onClose();
          } else if (card.type === 'lookup') {
            // Insert @cardname( and let user type the parameter
            const { state } = this.view;
            const { $from } = state.selection;
            const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\ufffc');
            const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9 ]*)$/);
            if (atMatch) {
              const deleteFrom = $from.pos - atMatch[2].length - 1;
              const tr = state.tr.replaceWith(deleteFrom, $from.pos, schema.text(`@${card.name}(`));
              this.view.dispatch(tr);
              this.view.focus();
            }
            onClose();
          } else {
            this._completeMention(card, null);
            onClose();
          }
        });
        dropdown.appendChild(item);
      });
    }

    // Position near cursor
    const editorRect = document.getElementById('editor').getBoundingClientRect();
    const coords = this.view.coordsAtPos($from.pos);
    dropdown.style.left = (coords.left - editorRect.left) + 'px';
    dropdown.style.top = (coords.bottom - editorRect.top + 4) + 'px';

    document.getElementById('editor').appendChild(dropdown);

    // Click outside to close
    const closeHandler = (e) => {
      if (!dropdown.contains(e.target)) {
        onClose();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 50);
  }

  _completeMention(card, mentionStartPos) {
    if (!this.view) return;
    const { state } = this.view;

    // Find the @ and query text to replace
    const { $from } = state.selection;
    const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\ufffc');
    const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9 ]*)$/);

    if (atMatch) {
      const deleteFrom = $from.pos - atMatch[2].length - 1; // @ + query
      const deleteTo = $from.pos;

      // Insert the card content based on type
      let insertNode;

      if (card.type === 'lookup') {
        // Lookup cards resolve to just the output value inline
        const inputValue = card._pendingInput || '';
        const outputValue = card._pendingOutput || '';
        delete card._pendingInput;
        delete card._pendingOutput;
        insertNode = schema.nodes.card_ref.create({
          ref: `@${card.name}(${inputValue})`,
          resolved: outputValue,
          cardName: card.name,
          inputValue: inputValue,
          outputValue: outputValue,
        });
      } else if (card.type === 'snippet' || card.type === 'text') {
        const content = card.data?.content || card.preview || card.name;
        insertNode = schema.text(content);
      } else if (card.type === 'date-stamp') {
        const dateText = this.cardEngine._resolveDateStamp(card);
        insertNode = schema.text(dateText);
      } else if (card.type === 'calculation') {
        // Insert as a card reference: {CardName}
        insertNode = schema.text(`{${card.name}}`);
      } else if (card.type === 'signature') {
        if (card.data?.dataUrl) {
          insertNode = schema.nodes.image.create({ src: card.data.dataUrl, alt: 'Signature' });
        } else {
          insertNode = schema.text(`[Signature: ${card.name}]`);
        }
      } else if (card.type === 'spreadsheet' && card.data?.gridLinked) {
        // Grid-linked named range — resolve live value and insert it
        const result = this.cardEngine.resolveReference(`{${card.name}}`);
        const value = result.resolved ? result.value : `{${card.name}}`;
        insertNode = schema.text(value);
      } else {
        // For tables, charts, templates — insert as card reference
        insertNode = schema.text(`{${card.name}}`);
      }

      const tr = state.tr.replaceWith(deleteFrom, deleteTo, insertNode);
      this.view.dispatch(tr);
      this.view.focus();
    }
  }

  _showLookupPicker(card, parentDropdown, onClose) {
    // Replace the dropdown contents with lookup values
    const values = this.cardEngine.getLookupValues(card);

    parentDropdown.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'padding:10px 12px;border-bottom:1px solid #eee;';
    header.innerHTML = `
      <div style="font-size:11px;color:#999;margin-bottom:4px;">Pick a value for</div>
      <div style="font-size:13px;font-weight:600;color:#1e40af;">\uD83D\uDD0D ${card.name}</div>
    `;
    parentDropdown.appendChild(header);

    if (values.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:10px 12px;font-size:12px;color:#999;';
      empty.textContent = 'No values found in lookup table';
      parentDropdown.appendChild(empty);
      return;
    }

    // Value items
    values.forEach((val, i) => {
      const item = document.createElement('div');
      item.className = 'mention-item' + (i === 0 ? ' active' : '');
      item.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:12px;display:flex;align-items:center;gap:10px;';
      item.innerHTML = `
        <span class="lookup-preview-input" style="font-weight:600;color:#333;min-width:60px;">${val.input}</span>
        <span style="color:#ccc;">\u2192</span>
        <span class="lookup-preview-output" style="color:#1e40af;font-weight:500;">${val.output}</span>
      `;
      item.addEventListener('mouseenter', () => {
        parentDropdown.querySelectorAll('.mention-item').forEach(i => i.classList.remove('active'));
        item.classList.add('active');
      });
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        card._pendingInput = val.input;
        card._pendingOutput = val.output;
        this._completeMention(card, null);
        parentDropdown.remove();
        onClose();
      });
      parentDropdown.appendChild(item);
    });

    // Custom input option
    const customDiv = document.createElement('div');
    customDiv.style.cssText = 'padding:8px 12px;border-top:1px solid #eee;';
    customDiv.innerHTML = `
      <input type="text" id="lookup-custom-input" placeholder="Or type a value..." style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:12px;box-sizing:border-box;">
    `;
    parentDropdown.appendChild(customDiv);

    setTimeout(() => {
      const input = document.getElementById('lookup-custom-input');
      if (input) {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            const customValue = input.value.trim();
            if (customValue) {
              const result = this.cardEngine.resolveLookup(card, customValue);
              card._pendingInput = customValue;
              card._pendingOutput = result.resolved ? result.outputValue : '?';
              this._completeMention(card, null);
              parentDropdown.remove();
              onClose();
            }
          }
          e.stopPropagation();
        });
      }
    }, 50);
  }

  // ── Feed/Result System: @cardname(value) = feed, @=cardname = result ──

  _findCardByName(name) {
    const lname = name.toLowerCase();
    const allLib = this.cardEngine.getAllLibraryCards ? this.cardEngine.getAllLibraryCards() : [];
    const allDoc = this.cardEngine.getAllCards();
    return [...allLib, ...allDoc].find(c => c.name.toLowerCase() === lname) || null;
  }

  // Insert a FEED node — shows the input value (e.g., "Scott"), clickable to change
  _insertInputNode(cardName, param, from, to) {
    const card = this._findCardByName(cardName);
    if (!card) return;

    // Resolve to get the output value (stored for linked result nodes)
    let outputValue = '?';
    if (card.type === 'lookup') {
      const result = this.cardEngine.resolveLookup(card, param);
      if (result && result.resolved) {
        outputValue = String(result.outputValue);
      } else {
        const values = this.cardEngine.getLookupValues(card);
        const match = values.find(v => v.input.toLowerCase() === param.toLowerCase());
        if (match) outputValue = String(match.output);
      }
    }

    // Store current input on the card so result nodes can read it
    card._currentInput = param;
    card._currentOutput = outputValue;

    const insertNode = schema.nodes.card_ref.create({
      ref: `@${cardName}(${param})`,
      resolved: param,
      cardName: cardName,
      inputValue: param,
      outputValue: outputValue,
      mode: 'input',
    });

    const tr = this.view.state.tr.replaceWith(from, to, insertNode);
    this.view.dispatch(tr);
    this.view.focus();

    // Update any existing result nodes for this card
    this._updateResultNodes(cardName, outputValue);

    // Start the green arrow cursor follower for placing the result
    this._startResultArrow(cardName, outputValue);
  }

  // Insert a RESULT node — shows the resolved value (e.g., "17"), auto-updates
  _insertOutputNode(card, from, to) {
    // Find the current input value from existing feed nodes in the doc
    const currentInput = this._findCurrentInput(card.name);
    let outputValue = '?';

    if (currentInput) {
      const result = this.cardEngine.resolveLookup(card, currentInput);
      if (result && result.resolved) {
        outputValue = String(result.outputValue);
      } else {
        const values = this.cardEngine.getLookupValues(card);
        const match = values.find(v => v.input.toLowerCase() === currentInput.toLowerCase());
        if (match) outputValue = String(match.output);
      }
    }

    const insertNode = schema.nodes.card_ref.create({
      ref: `@=${card.name}`,
      resolved: outputValue,
      cardName: card.name,
      inputValue: currentInput || '',
      outputValue: outputValue,
      mode: 'output',
    });

    const tr = this.view.state.tr.replaceWith(from, to, insertNode);
    this.view.dispatch(tr);
    this.view.focus();
  }

  // Find the current input value from feed nodes in the doc
  _findCurrentInput(cardName) {
    if (!this.view) return null;
    let found = null;
    this.view.state.doc.descendants((node) => {
      if (found) return false;
      if (node.type === schema.nodes.card_ref &&
          node.attrs.mode === 'input' &&
          node.attrs.cardName.toLowerCase() === cardName.toLowerCase()) {
        found = node.attrs.inputValue;
        return false;
      }
    });
    return found;
  }

  // Update all result nodes for a given card when the feed value changes
  _updateResultNodes(cardName, newOutputValue) {
    if (!this.view) return;
    const { state } = this.view;
    let tr = state.tr;
    let changed = false;

    state.doc.descendants((node, pos) => {
      if (node.type === schema.nodes.card_ref &&
          node.attrs.mode === 'output' &&
          node.attrs.cardName.toLowerCase() === cardName.toLowerCase()) {
        tr = tr.setNodeMarkup(pos, null, {
          ...node.attrs,
          outputValue: newOutputValue,
          resolved: newOutputValue,
        });
        changed = true;
      }
    });

    if (changed) {
      this.view.dispatch(tr);
    }
  }

  // Handle click on feed nodes — show picker to change value
  _setupFeedClickHandler() {
    const editorEl = document.getElementById('editor');
    if (!editorEl) return;

    editorEl.addEventListener('click', (e) => {
      const feedEl = e.target.closest('.card-feed');
      if (!feedEl) return;

      e.preventDefault();
      e.stopPropagation();

      const cardName = feedEl.dataset.cardName;
      const card = this._findCardByName(cardName);
      if (!card || card.type !== 'lookup') return;

      // Find the node position in the doc
      let targetPos = null;
      this.view.state.doc.descendants((node, pos) => {
        if (targetPos !== null) return false;
        if (node.type === schema.nodes.card_ref &&
            node.attrs.mode === 'input' &&
            node.attrs.cardName === cardName) {
          targetPos = pos;
          return false;
        }
      });

      if (targetPos === null) return;

      // Show picker dropdown at the feed element
      this._showFeedPicker(card, feedEl, targetPos);
    });
  }

  _showFeedPicker(card, anchorEl, nodePos) {
    // Remove any existing picker
    const existing = document.getElementById('feed-picker-dropdown');
    if (existing) existing.remove();

    const values = this.cardEngine.getLookupValues(card);
    if (values.length === 0) return;

    const dropdown = document.createElement('div');
    dropdown.id = 'feed-picker-dropdown';
    dropdown.style.cssText = 'position:fixed;z-index:9999;background:#fff;border:1px solid #ddd;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.15);max-height:200px;overflow-y:auto;min-width:180px;';

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'padding:8px 12px;border-bottom:1px solid #eee;font-size:11px;color:#999;';
    header.innerHTML = `Change <span style="color:#1e40af;font-weight:600;">@${card.name}</span> feed`;
    dropdown.appendChild(header);

    values.forEach((val) => {
      const item = document.createElement('div');
      item.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:13px;display:flex;align-items:center;gap:8px;';
      item.innerHTML = `<span style="font-weight:600;color:#333;">${val.input}</span><span style="color:#ccc;">\u2192</span><span style="color:#059669;">${val.output}</span>`;
      item.addEventListener('mouseenter', () => item.style.background = '#f3f4f6');
      item.addEventListener('mouseleave', () => item.style.background = 'none');
      item.addEventListener('click', (e) => {
        e.stopPropagation();

        // Update the feed node
        const { state } = this.view;
        let tr = state.tr.setNodeMarkup(nodePos, null, {
          ...state.doc.nodeAt(nodePos).attrs,
          inputValue: val.input,
          outputValue: val.output,
          resolved: val.input,
          ref: `@${card.name}(${val.input})`,
        });
        this.view.dispatch(tr);

        // Update all result nodes for this card
        this._updateResultNodes(card.name, val.output);

        dropdown.remove();
        this.view.focus();
      });
      dropdown.appendChild(item);
    });

    // Position below the feed element
    const rect = anchorEl.getBoundingClientRect();
    dropdown.style.left = rect.left + 'px';
    dropdown.style.top = (rect.bottom + 4) + 'px';
    document.body.appendChild(dropdown);

    // Click outside to close
    const close = (e) => {
      if (!dropdown.contains(e.target)) {
        dropdown.remove();
        document.removeEventListener('click', close);
      }
    };
    setTimeout(() => document.addEventListener('click', close), 50);
  }

  // ── Green Arrow: follows cursor after placing a feed, click to place result ──

  _startResultArrow(cardName, outputValue) {
    // Remove any existing arrow
    this._removeResultArrow();

    const arrow = document.createElement('div');
    arrow.id = 'result-arrow';
    arrow.innerHTML = '\u25B6'; // ▶ right-pointing triangle
    arrow.title = `Click to place result (${outputValue})`;
    arrow.style.cssText = `
      position: absolute;
      z-index: 9000;
      color: #10b981;
      font-size: 14px;
      cursor: pointer;
      pointer-events: auto;
      user-select: none;
      line-height: 1;
      transition: left 0.05s, top 0.05s;
      filter: drop-shadow(0 0 2px rgba(16, 185, 129, 0.5));
    `;

    // Store the pending result info
    this._pendingResult = { cardName, outputValue };

    // Click the arrow → insert result node at current cursor position
    arrow.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._placeResultAtCursor();
    });

    document.getElementById('editor').appendChild(arrow);
    this._resultArrow = arrow;

    // Start tracking cursor position
    this._resultArrowUpdater = () => this._updateResultArrowPosition();
    // Update on every transaction
    const origDispatch = this.view.props.dispatchTransaction;
    this._origDispatchForArrow = this.view.props.dispatchTransaction;

    // Use a polling approach that doesn't break the dispatch chain
    this._arrowPollInterval = setInterval(() => this._updateResultArrowPosition(), 50);

    // Initial position
    setTimeout(() => this._updateResultArrowPosition(), 10);
  }

  _updateResultArrowPosition() {
    if (!this._resultArrow || !this.view) return;
    try {
      const { state } = this.view;
      const { $from } = state.selection;
      const coords = this.view.coordsAtPos($from.pos);
      const editorRect = document.getElementById('editor').getBoundingClientRect();
      this._resultArrow.style.left = (coords.right - editorRect.left + 2) + 'px';
      this._resultArrow.style.top = (coords.top - editorRect.top) + 'px';
    } catch (e) { /* cursor position unavailable */ }
  }

  _placeResultAtCursor() {
    if (!this._pendingResult || !this.view) return;
    const { cardName, outputValue } = this._pendingResult;
    const card = this._findCardByName(cardName);

    // Re-resolve in case the feed value was changed
    let currentOutput = outputValue;
    if (card) {
      const currentInput = this._findCurrentInput(cardName);
      if (currentInput) {
        const result = this.cardEngine.resolveLookup(card, currentInput);
        if (result && result.resolved) {
          currentOutput = String(result.outputValue);
        }
      }
    }

    const insertNode = schema.nodes.card_ref.create({
      ref: `@=${cardName}`,
      resolved: currentOutput,
      cardName: cardName,
      inputValue: this._findCurrentInput(cardName) || '',
      outputValue: currentOutput,
      mode: 'output',
    });

    const { state } = this.view;
    const tr = state.tr.replaceSelectionWith(insertNode);
    this.view.dispatch(tr);
    this.view.focus();

    this._removeResultArrow();
  }

  _removeResultArrow() {
    if (this._resultArrow) {
      this._resultArrow.remove();
      this._resultArrow = null;
    }
    if (this._arrowPollInterval) {
      clearInterval(this._arrowPollInterval);
      this._arrowPollInterval = null;
    }
    this._pendingResult = null;
  }

  _showParamPicker(card, partialParam, $from, onClose) {
    // Remove existing dropdown
    const existing = document.getElementById('card-mention-dropdown');
    if (existing) existing.remove();

    const values = this.cardEngine.getLookupValues(card);
    const q = partialParam.toLowerCase();
    const filtered = q ? values.filter(v => v.input.toLowerCase().includes(q)) : values;

    if (filtered.length === 0 && !q) return;

    const dropdown = document.createElement('div');
    dropdown.id = 'card-mention-dropdown';
    dropdown.style.cssText = 'position:absolute;z-index:9000;background:#fff;border:1px solid #ddd;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.15);max-height:200px;overflow-y:auto;min-width:200px;';

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'padding:8px 12px;border-bottom:1px solid #eee;font-size:11px;color:#999;';
    header.innerHTML = `<span style="color:#1e40af;font-weight:600;">@${card.name}</span>(${partialParam}<span style="animation:blink 1s steps(1) infinite;">|</span>)`;
    dropdown.appendChild(header);

    (filtered.length > 0 ? filtered : values).forEach((val, i) => {
      const item = document.createElement('div');
      item.className = 'mention-item' + (i === 0 ? ' active' : '');
      item.style.cssText = 'padding:6px 12px;cursor:pointer;font-size:12px;display:flex;align-items:center;gap:8px;';
      item.innerHTML = `<span style="font-weight:600;color:#333;">${val.input}</span><span style="color:#ccc;">\u2192</span><span style="color:#059669;font-weight:500;">${val.output}</span>`;
      item.addEventListener('mouseenter', () => {
        dropdown.querySelectorAll('.mention-item').forEach(i => i.classList.remove('active'));
        item.classList.add('active');
      });
      item.addEventListener('click', () => {
        // Complete the function syntax: replace everything from @ through current cursor
        const { state } = this.view;
        const { $from: cur } = state.selection;
        const textBefore = cur.parent.textBetween(0, cur.parentOffset, null, '\ufffc');
        const funcPartMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9_\- ]+)\(([^)]*)$/);
        if (funcPartMatch) {
          const fullLength = funcPartMatch[0].length - funcPartMatch[1].length;
          const deleteFrom = cur.pos - fullLength;
          this._resolveInlineFunction(card.name, val.input, deleteFrom, cur.pos);
        }
        onClose();
      });
      dropdown.appendChild(item);
    });

    // Position near cursor
    const editorRect = document.getElementById('editor').getBoundingClientRect();
    const coords = this.view.coordsAtPos($from.pos);
    dropdown.style.left = (coords.left - editorRect.left) + 'px';
    dropdown.style.top = (coords.bottom - editorRect.top + 4) + 'px';
    document.getElementById('editor').appendChild(dropdown);

    const closeHandler = (e) => {
      if (!dropdown.contains(e.target)) {
        onClose();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 50);
  }

  // Insert card content from library panel "Insert" button
  _insertCardContent(card) {
    if (!this.view) return;

    // If the card panel's chooser set an insert text, use that directly
    if (card._insertText) {
      const text = card._insertText;
      delete card._insertText;
      const tr = this.view.state.tr.replaceSelectionWith(schema.text(text));
      this.view.dispatch(tr);
      this.view.focus();
      return;
    }

    let insertNode;

    if (card.type === 'snippet' || card.type === 'text') {
      const content = card.data?.content || card.preview || card.name;
      insertNode = schema.text(content);
    } else if (card.type === 'date-stamp') {
      const dateText = this.cardEngine._resolveDateStamp(card);
      insertNode = schema.text(dateText);
    } else if (card.type === 'calculation') {
      insertNode = schema.text(`{${card.name}}`);
    } else if (card.type === 'signature') {
      if (card.data?.dataUrl) {
        insertNode = schema.nodes.image.create({ src: card.data.dataUrl, alt: 'Signature' });
      } else {
        insertNode = schema.text(`[Signature: ${card.name}]`);
      }
    } else if (card.type === 'template') {
      // Instantiate template — insert all slot content
      const instances = this.cardEngine.instantiateTemplate(card.id);
      if (instances && instances.length > 0) {
        let tr = this.view.state.tr;
        instances.forEach(inst => {
          const text = inst.data?.content || inst.preview || inst.name;
          tr = tr.replaceSelectionWith(schema.node('paragraph', {}, [schema.text(text)]));
        });
        this.view.dispatch(tr);
        this.view.focus();
        return;
      }
      insertNode = schema.text(`{${card.name}}`);
    } else if (card.type === 'spreadsheet' && card.data?.gridLinked) {
      // Grid-linked named range — resolve live value
      const result = this.cardEngine.resolveReference(`{${card.name}}`);
      const value = result.resolved ? result.value : `{${card.name}}`;
      insertNode = schema.text(value);
    } else {
      // Tables, charts — insert as reference
      insertNode = schema.text(`{${card.name}}`);
    }

    const tr = this.view.state.tr.replaceSelectionWith(insertNode);
    this.view.dispatch(tr);
    this.view.focus();
  }

  // ── Phase 5: Export Menu & Docs→PDF Pipeline ──

  _showExportMenu() {
    // Remove existing menu if open
    const existing = document.querySelector('.export-menu-overlay');
    if (existing) { existing.remove(); return; }

    const overlay = document.createElement('div');
    overlay.className = 'export-menu-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:9998;';
    overlay.addEventListener('click', () => overlay.remove());

    const menu = document.createElement('div');
    menu.className = 'export-menu-dropdown';
    menu.style.cssText = `
      position:fixed; top:80px; right:40px; z-index:9999;
      background:#1e1e2e; border:1px solid #444; border-radius:8px;
      padding:6px 0; min-width:220px; box-shadow:0 8px 24px rgba(0,0,0,0.5);
      font-family:'Segoe UI',sans-serif; font-size:13px; color:#e0e0e0;
    `;

    const items = [
      { label: 'Send to PDF', icon: '📄', action: () => this._sendToPDF() },
      { label: 'Download as DOCX', icon: '📝', action: () => this._downloadDOCX() },
      { label: 'Download as HTML', icon: '🌐', action: () => this._downloadHTML() },
      { label: 'Print', icon: '🖨️', action: () => this._print() },
    ];

    items.forEach(item => {
      const btn = document.createElement('div');
      btn.style.cssText = `
        padding:8px 16px; cursor:pointer; display:flex; align-items:center; gap:10px;
      `;
      btn.innerHTML = `<span>${item.icon}</span><span>${item.label}</span>`;
      btn.addEventListener('mouseenter', () => btn.style.background = '#2a2a3e');
      btn.addEventListener('mouseleave', () => btn.style.background = 'none');
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        overlay.remove();
        item.action();
      });
      menu.appendChild(btn);
    });

    overlay.appendChild(menu);
    document.body.appendChild(overlay);
  }

  async _sendToPDF() {
    if (!this.view) return;
    if (!this._pdfViewer || !this._sidebar) {
      alert('PDF module not available.');
      return;
    }

    // 1. Clone doc content and resolve all {Card} references
    const resolvedHTML = this._resolveDocToHTML();

    // 2. Create a hidden render container styled like a printed page
    const container = document.createElement('div');
    container.id = 'pdf-render-container';
    container.style.cssText = `
      position:absolute; left:-9999px; top:0;
      width:816px; padding:72px;
      background:white; color:black;
      font-family:'Segoe UI', Arial, sans-serif;
      font-size:12pt; line-height:1.5;
    `;
    container.innerHTML = resolvedHTML;
    document.body.appendChild(container);

    try {
      // 3. Render to canvas with html2canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 816,
        windowWidth: 816,
      });

      // 4. Generate PDF with jsPDF — handle multi-page if content is tall
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF('p', 'pt', 'letter');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 0;

      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Split into pages if content exceeds one page
      const totalPages = Math.ceil(imgHeight / pageHeight);
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();

        // Create a slice of the canvas for this page
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        const sliceHeight = Math.min(
          canvas.height - i * (canvas.height / totalPages),
          canvas.height / totalPages
        );
        sliceCanvas.height = sliceHeight;

        const ctx = sliceCanvas.getContext('2d');
        ctx.drawImage(
          canvas,
          0, i * (canvas.height / totalPages),
          canvas.width, sliceHeight,
          0, 0,
          canvas.width, sliceHeight
        );

        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceImgHeight = (sliceHeight * imgWidth) / canvas.width;
        pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceImgHeight);
      }

      // 5. Get the PDF as ArrayBuffer and load into PDF viewer
      const pdfArrayBuffer = pdf.output('arraybuffer');
      this._pdfViewer.fileName = 'Document.pdf';
      await this._pdfViewer._loadPDF(pdfArrayBuffer);

      // 6. Switch to PDF module
      this._sidebar.switchModule('pdf');

    } catch (e) {
      console.error('Send to PDF failed:', e);
      alert('Failed to generate PDF: ' + e.message);
    } finally {
      container.remove();
    }
  }

  _resolveDocToHTML() {
    const doc = this.view.state.doc;
    const fragments = [];

    doc.forEach((node) => {
      fragments.push(this._nodeToHTML(node));
    });

    return fragments.join('');
  }

  _nodeToHTML(node) {
    if (node.isText) {
      let text = node.text || '';
      // Resolve {CardName} and {CardName.Field} references
      text = text.replace(/\{([^}]+)\}/g, (match) => {
        const result = this.cardEngine.resolveReference(match);
        if (result.resolved) {
          return `<span style="color:#1a73e8;font-weight:500">${result.value}</span>`;
        }
        return match;
      });
      // Apply marks
      if (node.marks && node.marks.length > 0) {
        node.marks.forEach(mark => {
          if (mark.type.name === 'strong') text = `<strong>${text}</strong>`;
          else if (mark.type.name === 'em') text = `<em>${text}</em>`;
          else if (mark.type.name === 'underline') text = `<u>${text}</u>`;
          else if (mark.type.name === 'strikethrough') text = `<s>${text}</s>`;
          else if (mark.type.name === 'code') text = `<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-family:monospace">${text}</code>`;
          else if (mark.type.name === 'textColor') text = `<span style="color:${mark.attrs.color}">${text}</span>`;
          else if (mark.type.name === 'highlight') text = `<span style="background:${mark.attrs.color}">${text}</span>`;
          else if (mark.type.name === 'link') text = `<a href="${mark.attrs.href}" style="color:#1a73e8">${text}</a>`;
        });
      }
      return text;
    }

    // card_ref atom — resolve and render inline
    if (node.type.name === 'card_ref') {
      const cardId = node.attrs.cardId;
      const card = this.cardEngine.getCard(cardId);
      if (card) {
        const result = this.cardEngine.resolveReference(`{${card.name}}`);
        const display = result.resolved ? result.value : card.name;
        return `<span style="color:#1a73e8;font-weight:500">${display}</span>`;
      }
      return `<span>[Card]</span>`;
    }

    // Gather children HTML
    const children = [];
    node.forEach(child => {
      children.push(this._nodeToHTML(child));
    });
    const inner = children.join('');

    // Block nodes
    const name = node.type.name;
    const attrs = node.attrs || {};

    if (name === 'heading') {
      const level = attrs.level || 1;
      const sizes = { 1: '24pt', 2: '20pt', 3: '16pt', 4: '14pt', 5: '12pt', 6: '11pt' };
      return `<h${level} style="font-size:${sizes[level]};margin:16px 0 8px;color:#111">${inner}</h${level}>`;
    }

    if (name === 'paragraph') {
      const align = attrs.align || 'left';
      return `<p style="margin:6px 0;text-align:${align}">${inner || '&nbsp;'}</p>`;
    }

    if (name === 'blockquote') {
      return `<blockquote style="border-left:3px solid #ccc;padding-left:12px;margin:8px 0;color:#555">${inner}</blockquote>`;
    }

    if (name === 'code_block') {
      return `<pre style="background:#f5f5f5;padding:12px;border-radius:4px;font-family:monospace;font-size:10pt;overflow-x:auto;margin:8px 0"><code>${inner}</code></pre>`;
    }

    if (name === 'bullet_list') {
      return `<ul style="margin:6px 0;padding-left:24px">${inner}</ul>`;
    }

    if (name === 'ordered_list') {
      return `<ol style="margin:6px 0;padding-left:24px">${inner}</ol>`;
    }

    if (name === 'list_item') {
      return `<li style="margin:2px 0">${inner}</li>`;
    }

    if (name === 'table') {
      return `<table style="border-collapse:collapse;width:100%;margin:8px 0">${inner}</table>`;
    }

    if (name === 'table_row') {
      return `<tr>${inner}</tr>`;
    }

    if (name === 'table_cell' || name === 'table_header') {
      const tag = name === 'table_header' ? 'th' : 'td';
      const bg = name === 'table_header' ? 'background:#f0f0f0;font-weight:600;' : '';
      return `<${tag} style="border:1px solid #ccc;padding:6px 10px;${bg}">${inner}</${tag}>`;
    }

    if (name === 'horizontal_rule') {
      return `<hr style="border:none;border-top:1px solid #ccc;margin:12px 0">`;
    }

    if (name === 'image') {
      const src = attrs.src || '';
      const alt = attrs.alt || '';
      return `<img src="${src}" alt="${alt}" style="max-width:100%;height:auto;margin:8px 0">`;
    }

    if (name === 'hard_break') {
      return '<br>';
    }

    // Fallback — wrap in div
    return inner ? `<div>${inner}</div>` : '';
  }

  _downloadDOCX() {
    // Generate a simple HTML-based DOCX using Blob
    const html = this._resolveDocToHTML();
    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8">
      <style>body{font-family:'Segoe UI',Arial,sans-serif;font-size:12pt;line-height:1.5;color:#111;}</style>
      </head><body>${html}</body></html>`;
    const blob = new Blob([docContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Document.doc';
    a.click();
    URL.revokeObjectURL(url);
  }

  _downloadHTML() {
    const html = this._resolveDocToHTML();
    const fullHTML = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Document</title>
      <style>body{font-family:'Segoe UI',Arial,sans-serif;font-size:12pt;line-height:1.5;color:#111;max-width:800px;margin:40px auto;padding:0 20px;}</style>
      </head><body>${html}</body></html>`;
    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Document.html';
    a.click();
    URL.revokeObjectURL(url);
  }
}
